import { Button, Center, Flex, HStack, Link, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useMemo } from "react"
import { NavLink } from "react-router-dom"
import { useMeQuery, UserFragment, UserRoleTypes, useUserPermissionsGroupsQuery } from "../../graphql"
import { checkForPermissions } from "../../utils"
import { Card, Stat } from "../common"

export type UserPermissionsGroupsProps = {
	user: UserFragment
}

export const UserPermissionsGroups: React.FC<UserPermissionsGroupsProps> = ({ user }) => {
	const [{ data, fetching, error }] = useUserPermissionsGroupsQuery({ variables: { userId: user._id } })

	const [{ data: meData }] = useMeQuery()

	const canUpdateUserPermissionsGroups = useMemo(() => {
		if (user.roleType === UserRoleTypes.Admin) {
			return checkForPermissions(meData?.me, "permissions/update-admin-user-permissions-group")[0]
		}

		if (user.roleType === UserRoleTypes.ShortTerm) {
			return checkForPermissions(meData?.me, "permissions/update-short-term-user-permissions-group")[0]
		}

		return checkForPermissions(meData?.me, "permissions/update-working-user-permissions-group")[0]
	}, [data, meData])

	return (
		<Card
			title="Permissions Groups"
			action={
				canUpdateUserPermissionsGroups && (
					<Button as={NavLink} to={`/users/${user._id}/updatePermissionsGroups`} colorScheme="primary" size="sm">
						Update
					</Button>
				)
			}
		>
			{fetching ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading permissions groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.userPermissionsGroups ? (
				<Flex justify="center" align="center" flexWrap="wrap">
					<Stat
						label="Groups"
						value={
							data.userPermissionsGroups.length ? (
								<HStack flexWrap="wrap" justify="center" maxW="xs">
									{data.userPermissionsGroups.map((g, i) => (
										<Link key={g._id} as={NavLink} to={`/permissionsGroups/${g._id}`}>
											{g.label.name}
											{i < (data.userPermissionsGroups?.length || 0) - 1 && ","}
										</Link>
									))}
								</HStack>
							) : (
								"Not available"
							)
						}
					/>
				</Flex>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find permissions groups.
					</Text>
				</Center>
			)}
		</Card>
	)
}
